import { type FC, useCallback, useEffect, useMemo } from "react";

import { cx } from "@emotion/css";
import { Flex } from "@mantine/core";
import { Control } from "@snapchat/snap-design-system";
import { CheckIcon, Colors } from "@snapchat/snap-design-system-icons";
import { useIntl } from "react-intl";

import { useBlizzardEvents } from "../../../analytics/hooks";
import { useFeatureGates } from "../../../cof-feature-gates/hooks";
import {
    CofConfigId,
    type DestinationType,
    type GoogleProtobufTimestamp,
    type MyLensesLensStatus,
    type MyLensesPreviewMedia,
    SurfaceType,
} from "../../../gql-generated/types";
import { LensPreview, PreviewValidationMessage } from "../../../lens-preview/components";
import { LensPreviewToBeReviewed } from "../../../lens-preview/components/messages.generated";
import { useLensPreviewState } from "../../../lens-preview/hooks";
import {
    GAME_LENS_VIDEO_DURATION_REQUIREMENT,
    getGameLensPreviewValidationMessage,
    validateGameLensPreview,
} from "../../../lens-preview/validation";
import { LensReleaseNotes } from "../../../lens-release-notes/components/LensReleaseNotes";
import { NotAvailableBar } from "../../../shared/components";
import { useShouldShowAdsAvailability } from "../../../shared/hooks";
import { isAvailableForAds } from "../../../shared/models";
import { type LensPreviewMediaItem } from "../../../shared/state";
import { detailsControl, detailsControlGap, detailsSeparator } from "../../../shared/styles";
import { timestampToDate } from "../../../shared/utils/date";
import { useFormatDetailsDate } from "../../hooks/useFormatDetailsDate";
import { useLensChangeTracker } from "../../hooks/useLensChangeTracker";
import { type LensActionsVisibilityState } from "../../state";
import { type ScanCode } from "../../utils/getLensScanCode";

import { AvailabilityDetails } from "./AvailabilityDetails";
import { AvailabilityItem } from "./AvailabilityItem";
import { LensCategories } from "./LensCategories";
import { LensDescription } from "./LensDescription";
import styles from "./LensDetailsSection.module.css";
import { LensScanCode } from "./lens-scan-code";
import { AvailabilityAds, AvailabilityLabel, AvailabilityTooltip } from "./messages.generated";

const BREAKPOINT = "xs";

interface LensDetailsSection {
    iconUrl: string;
    lensId: string;
    name: string;
    status: MyLensesLensStatus;
    updatedAt: GoogleProtobufTimestamp | undefined;
    primaryCategoryId: string;
    secondaryCategoryId: string;
    previewMediaList: readonly MyLensesPreviewMedia[];
    actionsConfig: LensActionsVisibilityState;
    disabledDestinationTypesList: readonly DestinationType[];
    surfaceType: readonly SurfaceType[];
    scanCode: ScanCode | undefined;
    shouldValidateGameLensPreview?: boolean;
    onGameLensPreviewSaveDisabledChange?: (disabled: boolean) => void;
}

export const LensDetailsSection: FC<LensDetailsSection> = ({
    iconUrl,
    name,
    status,
    updatedAt,
    primaryCategoryId,
    secondaryCategoryId,
    lensId,
    actionsConfig,
    disabledDestinationTypesList,
    surfaceType,
    scanCode,
    shouldValidateGameLensPreview = false,
    onGameLensPreviewSaveDisabledChange,
}) => {
    const intl = useIntl();
    const { sendBlizzardEvent: sendBlizzardEventHandler } = useBlizzardEvents();
    const isSpectaclesOnly = surfaceType.length === 1 && surfaceType.includes(SurfaceType.SurfaceTypeSpectacles);
    const hasSpectacles = surfaceType.includes(SurfaceType.SurfaceTypeSpectacles);

    const { previewMediaItems, updatePreviewMedia, hasFieldChanged } = useLensChangeTracker(lensId);

    const { configs } = useFeatureGates();
    const isLensDescriptionEnabled = configs[CofConfigId.MyLensesLensDescription].enabled;
    const isReleaseNotesEnabled = configs[CofConfigId.MyLensesSpectaclesRnTagsMultiPreviews].enabled;

    const { shouldShowAdsAvailability } = useShouldShowAdsAvailability();

    const setPreviewMediaItems = useCallback(
        (items: LensPreviewMediaItem[]) => {
            updatePreviewMedia(items);
        },
        [updatePreviewMedia],
    );

    const { uploadFileLoading, ...lensPreviewProps } = useLensPreviewState({
        status: status,
        previewMediaItems: previewMediaItems ?? [],
        setPreviewMediaItems,
        videoDurationRequirement: shouldValidateGameLensPreview ? GAME_LENS_VIDEO_DURATION_REQUIREMENT : undefined,
    });

    const gameLensPreviewValidationMessage = useMemo(() => {
        if (!shouldValidateGameLensPreview || !hasFieldChanged("previewMediaItems")) {
            return null;
        }

        const validationError = validateGameLensPreview({
            previewMediaItems: previewMediaItems ?? [],
            previewValidationMessage: lensPreviewProps.validationMessage,
        });
        const errorMessage = getGameLensPreviewValidationMessage(validationError);

        return errorMessage ? intl.formatMessage(errorMessage) : null;
    }, [shouldValidateGameLensPreview, hasFieldChanged, previewMediaItems, lensPreviewProps.validationMessage, intl]);

    const lastUpdatedAtDate = useFormatDetailsDate(timestampToDate(updatedAt));

    const availableForAds = isAvailableForAds(disabledDestinationTypesList, status);

    sendBlizzardEventHandler({ event: "MYLENSES_MANAGEMENT_PAGE_VIEW" });

    const finalizeAndSubmitChanges = useCallback(() => {
        lensPreviewProps.finalizeChanges();
    }, [lensPreviewProps]);

    useEffect(() => {
        onGameLensPreviewSaveDisabledChange?.(Boolean(gameLensPreviewValidationMessage));
    }, [onGameLensPreviewSaveDisabledChange, gameLensPreviewValidationMessage]);

    return (
        <>
            <Flex direction={{ base: "column", [BREAKPOINT]: "row" }} gap={{ base: "md", [BREAKPOINT]: "lg" }}>
                <div className={styles.previewSection}>
                    <Flex
                        direction={{ base: "row", [BREAKPOINT]: "column" }}
                        gap="md"
                        align={{ base: "start", [BREAKPOINT]: "stretch" }}
                    >
                        <LensPreview
                            {...lensPreviewProps}
                            logoSrc={iconUrl}
                            uploadFileLoading={uploadFileLoading}
                            finalizeChanges={finalizeAndSubmitChanges}
                            disabled={!actionsConfig.setPreview}
                            surfaceType={surfaceType}
                            badgeText={hasFieldChanged("previewMediaItems") ? <LensPreviewToBeReviewed /> : undefined}
                        />
                        <PreviewValidationMessage
                            className={styles.previewValidationMessage}
                            validationMessage={
                                lensPreviewProps.modalProps.visible ? null : gameLensPreviewValidationMessage
                            }
                        />
                    </Flex>
                </div>
                <Flex direction="column" flex={1}>
                    <Flex gap="md" justify="space-between" wrap="wrap">
                        <Control label="Lens Name" className={detailsControl}>
                            {name}
                        </Control>
                        <Control label="Last Updated" className={detailsControl}>
                            {lastUpdatedAtDate}
                        </Control>
                    </Flex>
                    {surfaceType.includes(SurfaceType.SurfaceTypeSpectacles) && isLensDescriptionEnabled && (
                        <LensDescription disabled={!actionsConfig.setLensDescription} lensId={lensId} />
                    )}
                    {shouldShowAdsAvailability && !isSpectaclesOnly && (
                        <Control
                            label={<AvailabilityLabel />}
                            tooltip={<AvailabilityTooltip />}
                            className={cx(detailsControl, detailsControlGap)}
                        >
                            <AvailabilityItem
                                text={<AvailabilityAds />}
                                icon={availableForAds ? <CheckIcon fill={Colors.Green.V200} /> : <NotAvailableBar />}
                            />
                            {!availableForAds && <AvailabilityDetails />}
                        </Control>
                    )}
                    <hr className={detailsSeparator} />
                    {isReleaseNotesEnabled && hasSpectacles && (
                        <>
                            <LensReleaseNotes lensId={lensId} />
                            <hr className={detailsSeparator} />
                        </>
                    )}
                    <LensCategories primaryCategoryId={primaryCategoryId} secondaryCategoryId={secondaryCategoryId} />
                    <hr className={detailsSeparator} />
                    {scanCode && !isSpectaclesOnly && (
                        <LensScanCode scanCode={scanCode} isVisibleDownload={actionsConfig.downloadQrCode} />
                    )}
                </Flex>
            </Flex>
        </>
    );
};
